import React from 'react'
import Typography from '../Typography/Typography'
import ThemeConfig from '../../theme/ThemeConfig'
import {
  Paper,
  TextField,
  Chip,
  ThemeProvider,
  StyledEngineProvider,
  createTheme,
} from '@mui/material'
import {
  Cancel as CancelIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material'
import { isEmpty, map } from 'lodash'
import { styled } from '@mui/material/styles'
import { emphasize } from '@mui/material'

const inputBoxTheme = createTheme({
  overrides: {
    MuiFormLabel: {
      root: {
        lineHeight: 0,
        fontSize: ThemeConfig.typography.inputText.fontSize,
        color: ThemeConfig.palette.secondary.main,
        '&$focused': {
          color: ThemeConfig.palette.defaultGrey.dark,
          lineHeight: 1,
        },
        '&.Mui-error': {
          color: ThemeConfig.palette.error.main,
        },
      },
      focused: {},
    },
    MuiInputLabel: {
      shrink: {
        lineHeight: 1,
        fontSize: ThemeConfig.typography.inputText.fontSize,
        color: ThemeConfig.palette.secondary.main,
        '&.Mui-error': {
          color: ThemeConfig.palette.error.main,
        },
      },
    },
    MuiOutlinedInput: {
      notchedOutline: {
        borderColor: ThemeConfig.palette.secondary.darkest,
      },
      root: {
        '&:hover:not($disabled):not($focused):not($error) $notchedOutline': {
          borderColor: ThemeConfig.palette.secondary.darkest,
        },
        '&$focused $notchedOutline': {
          borderColor: ThemeConfig.palette.secondary.darkest,
        },
        '&$disabled': {
          backgroundColor: ThemeConfig.palette.inputDisabled.backgroundColor,
        },
        '&.Mui-error': {
          color: ThemeConfig.palette.error.main,
          '&.Mui-focused fieldset': {
            borderColor: ThemeConfig.palette.error.main,
          },
        },
      },
    },
    MuiFormHelperText: {
      root: {
        fontStyle: 'italic',
        '&.Mui-error': {
          color: ThemeConfig.palette.error.main,
        },
      },
    },
  },
})

const arrowDropDownTheme = createTheme({
  overrides: {
    MuiSvgIcon: {
      root: {
        color: '#0000008a',
      },
      colorPrimary: {
        color: '#0000008a',
      },
      colorError: {
        color: ThemeConfig.palette.error.main,
      },
      colorDisabled: {
        color: ThemeConfig.palette.inputDisabled.backgroundColor,
      },
    },
  },
})

const StyledNoOptionsMessage = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  textAlign: 'center',
}))

const NoOptionsMessage = props => {
  const selectedValues = map(props.getValue(), 'value')
  const options = map(props.options, 'value')
  const isAllSelected =
    options.filter(i => !selectedValues.includes(i)).length === 0
  const isLoading =
    props.selectProps.inputValue.trim() === '' &&
    (!isAllSelected || !props.options.length)

  return (
    <StyledNoOptionsMessage
      variant="body1"
      color="textSecondary"
      {...props.innerProps}
    >
      {isLoading ? 'Loading...' : props.children}
    </StyledNoOptionsMessage>
  )
}

const inputComponent = ({ inputRef, ...props }) => {
  return <div ref={inputRef} {...props} />
}

const Control = props => {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: {
      classes,
      TextFieldProps,
      value,
      isRequired,
      helperText,
      errorMessage,
      isDisabled,
    },
  } = props

  const isError = isRequired && isEmpty(value) && !isDisabled
  const helperMessage = isError ? errorMessage : helperText
  TextFieldProps.InputLabelProps = {
    shrink: !isEmpty(value) || !!props.isFocused,
    error: isError,
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={inputBoxTheme}>
        <TextField
          fullWidth
          variant="outlined"
          disabled={props.isDisabled}
          error={isError}
          helperText={helperMessage}
          InputProps={{
            inputComponent,
            inputProps: {
              style: classes.input,
              ref: innerRef,
              onClick: props.selectProps.onClick,
              children,
              ...innerProps,
            },
          }}
          {...TextFieldProps}
        />
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

const StyledPlaceholderTypography = styled(Typography)(() => ({
  position: 'absolute',
  left: 15,
  bottom: 10,
  fontSize: ThemeConfig.typography.body1.fontSize,
}))

const Placeholder = props => {
  const { innerProps = {}, children } = props
  return (
    <StyledPlaceholderTypography
      variant="body1"
      color="textSecondary"
      {...innerProps}
    >
      {children}
    </StyledPlaceholderTypography>
  )
}

const StyledContainer = styled('div')(({ theme, isMulti }) => ({
  display: 'flex',
  flex: 1,
  paddingLeft: 5,
  alignItems: 'center',
  width: 'inherit',
  whiteSpace: 'nowrap',
  overflow: 'auto',
  ...(isMulti && {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    paddingLeft: 10,
    paddingTop: 5,
    alignItems: 'center',
    width: 'inherit',
  }),
}))

const ValueContainer = props => {
  return (
    <StyledContainer isMulti={props.isMulti}>{props.children}</StyledContainer>
  )
}

const StyledChip = styled(Chip)(({ theme, isFocused }) => ({
  margin: theme.spacing(0.25, 0.25),
  fontSize: ThemeConfig.typography.body1.fontSize,
  ...(isFocused && {
    backgroundColor: emphasize(
      theme.palette.type === 'light'
        ? theme.palette.grey[300]
        : theme.palette.grey[700],
      0.08,
    ),
  }),
}))

const MultiValue = props => {
  const label = props.selectProps.getChipLabel
    ? props.selectProps.getChipLabel(props.children)
    : props.children

  return (
    <StyledChip
      tabIndex={-1}
      size="small"
      label={label}
      isFocused={props.isFocused}
      isDisabled={props.isDisabled}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  )
}

const StyledSingleValueTypography = styled(Typography)(
  ({ theme, isDisabled }) => ({
    fontSize: ThemeConfig.typography.body1.fontSize,
    ...(isDisabled && { color: ThemeConfig.palette.secondary.dark }),
  }),
)

const SingleValue = props => {
  return (
    <StyledSingleValueTypography
      variant="body1"
      isDisabled={props.selectProps.isDisabled}
    >
      {props.children}
    </StyledSingleValueTypography>
  )
}

const StyledPaper = styled(Paper)((theme, isMenuTop) => ({
  left: 0,
  right: 0,
  zIndex: 1999,
  position: 'absolute',
  marginBottom: '10px',
  color: 'red',
  ...(isMenuTop && {
    position: 'absolute',
    zIndex: 999,
    marginTop: theme.spacing(0.5),
    left: 0,
    right: 0,
  }),
}))

const Menu = props => {
  const { menuPlacement } = props.selectProps
  return (
    <StyledPaper
      square
      isMenuTop={menuPlacement && menuPlacement.toLowerCase() === 'top'}
      {...props.innerProps}
    >
      {props.children}
    </StyledPaper>
  )
}

const IndicatorSeparator = () => {
  return null
}

const DropdownIndicator = props => {
  const color = props.isDisabled ? 'disabled' : 'primary'
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={arrowDropDownTheme}>
        <ArrowDropDownIcon color={color} />
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

export {
  IndicatorSeparator,
  Control,
  MultiValue,
  NoOptionsMessage,
  ValueContainer,
  SingleValue,
  Placeholder,
  Menu,
  DropdownIndicator,
}
